import * as React from "react";
import {translate} from 'react-i18next';
import * as _ from "lodash";

import SelectField from "../../components/SelectField";
import Button from "../../components/Button";
import Dialog from "../../components/Dialogs/Dialog";
import {SelectWorkflowOB, SelectedWorkflowOB} from "../../components/Table/VirtualizedTableInterfaces";
import {WorkflowDto, OptionBasicDto} from "../../services/Client";
import {ITranslatedComponent} from "../../redux/reducers/generalReducer";
import {Form, TextArea, Flag, Icon} from "semantic-ui-react";
import Collapsible from "react-collapsible";
import PromtFormatter from "src/components/Table/components/reactQuill/PromtFormatter";

interface IInitPSLDialogProps extends ITranslatedComponent {
    /** Data source for base option/workflow selection */
    oBworkflows: SelectWorkflowOB[];

    /** Base option with only one workflow */
    preSelectedWorkflows: SelectedWorkflowOB[];

    /** List of base options that cannot be initialized */
    notInitializableOBs: OptionBasicDto[];

    /** Selected type family id */
    typeFamilyId: number;

    /** Close dialog and pass the selected argument. */
    onSel: (dto: SelectedWorkflowOB[], typeFamilyId?: number) => any;

    /** Close dialog without return value. */
    onClose: () => any;
}

interface IInitPSLDialogState {
    /** The selected base option/workflow items. */
    selectedWorkflows?: SelectedWorkflowOB[];
}

class InitPSLDialog extends React.Component<IInitPSLDialogProps, IInitPSLDialogState> {
    constructor(props: IInitPSLDialogProps) {
        super(props);

        this.state = {
            selectedWorkflows: [],
        };
    }

    isSelectionValid = (): boolean => {
        const {selectedWorkflows} = this.state;
        return selectedWorkflows !== undefined && selectedWorkflows.length === this.props.oBworkflows.length;
    }

    onSel = () => {
        if (!this.props.onSel) {
            return;
        }

        const {selectedWorkflows} = this.state;
        if (selectedWorkflows === undefined) {
            return;
        }

        this.props.preSelectedWorkflows && this.props.typeFamilyId ?
            this.props.onSel(selectedWorkflows.concat(this.props.preSelectedWorkflows), this.props.typeFamilyId)
            : this.props.onSel(selectedWorkflows, this.props.typeFamilyId);
    }

    setSelectedW(value: SelectedWorkflowOB): void {
        let tw: SelectedWorkflowOB[] = this.state.selectedWorkflows ? this.state.selectedWorkflows : [];

        const found: SelectedWorkflowOB | undefined = tw.filter((t) => t.optionBasic === value.optionBasic)[0];
        if (found) {
            tw.splice(tw.indexOf(found), 1);
            tw.push(value);
        }
        else {
            tw.push(value);
        }
        this.setState({selectedWorkflows: tw});
    }

    render() {
        return (
            <Dialog
                open={true}
                title={this.props.t("BASEOPTIONS:INIT_PSL_DLG_TITLE")}
                customStyleClassName={"InitOBDialog"}
                actions={this.props.oBworkflows.length > 0 || this.props.preSelectedWorkflows.length > 0 ?
                    [
                        <Button
                            key={"submitButton"}
                            icon={"checkmark"}
                            disabled={!this.isSelectionValid()}
                            onClick={this.onSel}
                        />,
                        <Button
                            key={"cancelButton"}
                            icon={"ban"}
                            onClick={this.props.onClose}
                        />
                    ]
                    :
                    [
                        <Button
                            key={"cancelButton"}
                            icon={"ban"}
                            onClick={this.props.onClose}
                        />
                    ]
                }
            >
                {/** Overlapping Workflows */}
                {this.props.oBworkflows.length > 0 &&
                    <Collapsible
                        open={true}
                        className={"collapsible"}
                        trigger={
                            <h4 style={{'backgroundColor': 'whitesmoke'}}>
                                <Icon name='tasks' size='small' />
                                {this.props.t("BASEOPTIONS:OVERLAPPING_WORKFLOWS_TITLE")}
                                <Icon name='angle double down' size='small' style={{float: 'right'}} />
                            </h4>}
                    >
                        <div style={{'backgroundColor': 'whitesmoke'}}>{this.props.t("BASEOPTIONS:OVERLAPPING_WORKFLOWS_MSG")}</div>
                        <br />
                        <div>
                            {_.map(this.props.oBworkflows, (ob, obIndex) => (
                                <div key={'init-psl-workflows-' + obIndex}>
                                    <br />
                                    {this.props.t("BASEOPTIONS:OPTION_CODE") + " "}
                                    <span className="initOBCode">{ob.optionBasic.optionCode ? ob.optionBasic.optionCode : "undefined"}</span>
                                    {":"}
                                    <SelectField
                                        placeholder={this.props.t("BASEOPTIONS:SELECT_WORKFLOW")}
                                        clearable={false}
                                        dataSource={ob.workflows}
                                        value={ob}
                                        onChange={(selectedValue: WorkflowDto, index: number) => {
                                            const roles = {optionBasic: ob.optionBasic, workflow: selectedValue} as SelectedWorkflowOB;
                                            this.setSelectedW(roles);
                                        }}
                                        rendererKey={(value: WorkflowDto) => value.name}
                                    />
                                    <br />
                                    <div className="OBTextForm">
                                        <Form>
                                            {ob.optionBasic.texts.sort((a, b) => (a.languageDto && b.languageDto && a.languageDto.code > b.languageDto.code) ? 1 : -1)
                                            .map((item, index) =>
                                                <Form.Group widths='equal' key={obIndex + "formItem" + index}>
                                                    <Form.Field
                                                        readOnly={true}
                                                        label={<p>{this.props.t("COLUMN_FACTORY:SHORTTEXT")}
                                                            <Flag
                                                                className="InitPSLDialogTextLangFlag"
                                                                name={item.languageDto && item.languageDto.code === "de" ? "de" : "gb"}
                                                            />
                                                        </p>}
                                                        control={TextArea}
                                                        value={item.shorttext || ""}
                                                    />
                                                    <Form.Field
                                                        className="with-border-quill-initpsl"
                                                        readOnly={true}
                                                        label={<p>{this.props.t("COLUMN_FACTORY:LONGTEXT")}
                                                            <Flag
                                                                className="InitPSLDialogTextLangFlag"
                                                                name={item.languageDto && item.languageDto.code === "de" ? "de" : "gb"}
                                                            />
                                                        </p>}
                                                        control={PromtFormatter}
                                                        value={item.longtext || ""}
                                                    />
                                                </Form.Group>)}
                                        </Form>
                                        <br />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <br />
                    </Collapsible>
                }

                {this.props.oBworkflows.length > 0 && (this.props.preSelectedWorkflows.length > 0 || this.props.notInitializableOBs.length > 0) && <hr />}

                {/** OBs that cannot be initiliazed because they are already assigned to PSL */}
                {this.props.notInitializableOBs.length > 0 &&
                    <Collapsible
                        open={true}
                        className={"collapsible"}
                        trigger={
                            <h4 style={{'backgroundColor': 'whitesmoke'}}>
                                <Icon name='ban' color='red' size='small' />
                                {this.props.t("BASEOPTIONS:OB_ALREADY_INIT_TITLE")}
                                <Icon name='angle double down' size='small' style={{float: 'right'}} />
                            </h4>}
                    >
                        <div style={{'backgroundColor': 'whitesmoke'}}>{this.props.t("BASEOPTIONS:OB_ALREADY_INIT_MSG")}</div>
                        <br />
                        <div>
                            {_.map(this.props.notInitializableOBs, (nOb, nObIndex) => (
                                <div key={'not-init-ob-' + nObIndex}>
                                    <br />
                                    {this.props.t("BASEOPTIONS:OPTION_CODE") + " "}<span className="notInitOBCode">{nOb.optionCode ? nOb.optionCode : "undefined"}</span> {":"}
                                    <div className="OBTextForm">
                                        <br />
                                        <Form>
                                            {nOb.texts.sort((a, b) => (a.languageDto && b.languageDto && a.languageDto.code > b.languageDto.code) ? 1 : -1)
                                            .map((item, index) =>
                                                <Form.Group widths='equal' key={nObIndex + "formItem" + index}>
                                                    <Form.Field
                                                        readOnly={true}
                                                        label={<p>{this.props.t("COLUMN_FACTORY:SHORTTEXT")}
                                                            <Flag
                                                                className="InitPSLDialogTextLangFlag"
                                                                name={item.languageDto && item.languageDto.code === "de" ? "de" : "gb"}
                                                            />
                                                        </p>}
                                                        control={TextArea}
                                                        value={item.shorttext || ""}
                                                    />
                                                    <Form.Field
                                                        className="with-border-quill-initpsl"
                                                        readOnly={true}
                                                        label={<p>{this.props.t("COLUMN_FACTORY:LONGTEXT")}
                                                            <Flag
                                                                className="InitPSLDialogTextLangFlag"
                                                                name={item.languageDto && item.languageDto.code === "de" ? "de" : "gb"}
                                                            />
                                                        </p>}
                                                        control={PromtFormatter}
                                                        value={item.longtext || ""}
                                                    />
                                                </Form.Group>
                                            )}
                                        </Form>
                                        <br />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Collapsible>
                }

                {this.props.notInitializableOBs.length > 0 && this.props.preSelectedWorkflows.length > 0 && <hr />}

                {/** Preselected Workflows(only one Wf matches the init criteria) */}
                {this.props.preSelectedWorkflows.length > 0 &&
                    <Collapsible
                        className={"collapsible"}
                        trigger={
                            <h4 style={{'backgroundColor': 'whitesmoke'}}>
                                <Icon name='thumbtack' size='small' />
                                {this.props.t("BASEOPTIONS:PRESELECTED_WORKFLOWS_TITLE")}
                                <Icon name='angle double down' size='small' style={{float: 'right'}} />
                            </h4>}
                    >
                        <div style={{'backgroundColor': 'whitesmoke'}}>{this.props.t("BASEOPTIONS:PRESELECTED_WORKFLOWS_MSG")}</div>
                        <br />
                        <div>
                            {_.map(this.props.preSelectedWorkflows, (pOb, pObIndex) => (
                                <div key={'init-psl-presel-workflows-' + pObIndex}>
                                    <br />
                                    {this.props.t("BASEOPTIONS:OPTION_CODE") + " "}
                                    <span className="initOBCode">{pOb.optionBasic.optionCode ? pOb.optionBasic.optionCode : "undefined"}</span>
                                    {":"}
                                    <SelectField
                                        disabled={true}
                                        dataSource={pOb.workflow ? [pOb.workflow] : []}
                                        value={pOb.workflow ? pOb.workflow : undefined}
                                        rendererKey={(value: WorkflowDto) => value.name}
                                    />
                                    <br />
                                    <div className="OBTextForm">
                                        <Form>
                                            {pOb.optionBasic.texts.sort((a, b) => (a.languageDto && b.languageDto && a.languageDto.code > b.languageDto.code) ? 1 : -1)
                                            .map((item, index) =>
                                                <Form.Group widths='equal' key={pObIndex + "formItem" + index}>
                                                    <Form.Field
                                                        readOnly={true}
                                                        label={<p>{this.props.t("COLUMN_FACTORY:SHORTTEXT")}
                                                            <Flag
                                                                className="InitPSLDialogTextLangFlag"
                                                                name={item.languageDto && item.languageDto.code === "de" ? "de" : "gb"}
                                                            />
                                                        </p>}
                                                        control={TextArea}
                                                        value={item.shorttext || ""}
                                                    />
                                                    <Form.Field
                                                        readOnly={true}
                                                        className="with-border-quill-initpsl"
                                                        label={<p>{this.props.t("COLUMN_FACTORY:LONGTEXT")}
                                                            <Flag
                                                                className="InitPSLDialogTextLangFlag"
                                                                name={item.languageDto && item.languageDto.code === "de" ? "de" : "gb"}
                                                            />
                                                        </p>}
                                                        control={PromtFormatter}
                                                        value={item.longtext || ""}
                                                    />
                                                </Form.Group>)}
                                        </Form>
                                        <br />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <br />
                    </Collapsible>
                }
            </Dialog>
        );
    }
}

export default translate(["BASEOPTIONS", "COLUMN_FACTORY"])(InitPSLDialog);



// WEBPACK FOOTER //
// ./src/views/BaseOptionsView/InitPslDialog.tsx